import React, { useState, useEffect } from 'react';
import { Card, Col, Form, Input, Row } from 'antd';
import styled from '@emotion/styled';

interface AnswerOption {
  id?: string;
  title: string;
  subTitle?: string;
  imageLink?: string;
}

interface Props {
  t: (key: string, opts?: any) => string;
  answerOptions: AnswerOption[];
  onOptionChange: (index: number, field: keyof AnswerOption, value: string) => void;
  readOnly?: boolean;
}

const ImageTileAnswers: React.FC<Props> = ({ t, answerOptions, onOptionChange, readOnly = false }) => {
  const [charCounts, setCharCounts] = useState<{ [key: string]: number }>({});

  const maxLength = 42;

  const handleCharCountChange = (optionId: string, value: string) => {
    setCharCounts(prev => ({
      ...prev,
      [optionId]: value.length,
    }));
  };

  const handleInputChange = (index: number, field: keyof AnswerOption, value: string) => {
    if (field === 'title') {
      const truncatedValue = value.slice(0, maxLength);
      onOptionChange(index, field, truncatedValue);
      handleCharCountChange(answerOptions[index].id || `option-${index}`, truncatedValue);
      return;
    }

    // Do not truncate imageLink to allow full URL
    onOptionChange(index, field, value);
  };

  useEffect(() => {
    const initialCounts: { [key: string]: number } = {};
    answerOptions.forEach((option, index) => {
      const optionId = option.id || `option-${index}`;
      initialCounts[optionId] = option.title.length;
    });
    setCharCounts(initialCounts);
  }, [answerOptions]);

  return (
    <StyledAnswerSection>
      <Row gutter={[16, 8]}>
        {answerOptions.map((option, index) => {
          const optionId = option.id || `option-${index}`;
          const currentCount = charCounts[optionId] || 0;

          return (
            <Col span={24} key={option.id}>
              <StyledAnswerCard>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label={`${t('questionForm.labels.answerTitle')} ${index + 1}`} required={index < 2}>
                      <div className="position-relative">
                        <Input
                          value={option.title}
                          onChange={e => handleInputChange(index, 'title', e.target.value)}
                          placeholder={t('questionForm.placeholders.answerOption')}
                          maxLength={maxLength}
                          disabled={readOnly}
                        />
                        <div className="count-item">{`${currentCount}/${maxLength}`}</div>
                      </div>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('questionForm.labels.imageUrlRequired')}>
                      <Input
                        value={option.imageLink}
                        onChange={e => handleInputChange(index, 'imageLink', e.target.value)}
                        placeholder={t('questionForm.placeholders.imageUrl')}
                        disabled={readOnly}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </StyledAnswerCard>
            </Col>
          );
        })}
      </Row>
    </StyledAnswerSection>
  );
};

const StyledAnswerSection = styled.div`
  margin-bottom: 24px;

  .position-relative {
    position: relative;
  }

  .count-item {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: #cbcbcb;
    pointer-events: none;
  }

  .position-relative input {
    padding-right: 40px;
  }
`;

const StyledAnswerCard = styled(Card)`
  border: none;

  .ant-card-body {
    padding: 0 16px;
  }
`;

export default ImageTileAnswers;
